import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { fetchWithAuth } from "../utils/api";
import "./AiAssistant.css";

const AiAssistant = ({ hideButton = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Scroll vers le bas quand nouveaux messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Focus sur l'input quand on ouvre
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage("");
    setIsLoading(true);

    // Ajoute le message utilisateur à l'historique
    const newHistory = [...history, { role: "user", content: userMessage }];
    setHistory(newHistory);

    try {
      const res = await fetchWithAuth(`${apiUrl}/ai/chat`, {
        method: "POST",
        body: JSON.stringify({
          message: userMessage,
          history: history // Envoie l'historique précédent (sans le nouveau message)
        }),
      });

      if (!res.ok) {
        throw new Error("Erreur de l'assistant");
      }

      const data = await res.json();
      setHistory([...newHistory, { role: "assistant", content: data.response }]);

    } catch (err) {
      console.error("Erreur assistant:", err);
      setHistory([
        ...newHistory,
        { role: "assistant", content: t('aiAssistant.error') }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Cache le bouton quand le panneau filtres est ouvert (sauf si le chat AI est déjà ouvert)
  const showButton = !hideButton || isOpen;

  return (
    <>
      {/* Bouton flottant */}
      {showButton && (
        <button
          className={`ai-fab ${isOpen ? "ai-fab--open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t('aiAssistant.toggle')}
        >
          {isOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <path d="M12 7v2M12 13h.01"></path>
            </svg>
          )}
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="ai-panel">
          <div className="ai-header">
            <div className="ai-header-title">
              <span>{t('aiAssistant.title')}</span>
            </div>
            <button className="ai-close" onClick={() => setIsOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="ai-messages">
            {history.length === 0 && (
              <div className="ai-welcome">
                <p>{t('aiAssistant.welcome')}</p>
                <div className="ai-suggestions">
                  <button onClick={() => setMessage(t('aiAssistant.suggestions.howToCreate'))}>
                    {t('aiAssistant.suggestions.howToCreate')}
                  </button>
                  <button onClick={() => setMessage(t('aiAssistant.suggestions.fillStation'))}>
                    {t('aiAssistant.suggestions.fillStation')}
                  </button>
                  <button onClick={() => setMessage(t('aiAssistant.suggestions.upcomingDives'))}>
                    {t('aiAssistant.suggestions.upcomingDives')}
                  </button>
                </div>
              </div>
            )}

            {history.map((msg, i) => (
              <div key={i} className={`ai-message ai-message--${msg.role}`}>
                <div className="ai-message-content">
                  {msg.role === "assistant" ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="ai-message ai-message--assistant">
                <div className="ai-message-content ai-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="ai-input-form" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('aiAssistant.placeholder')}
              rows={1}
              disabled={isLoading}
            />
            <button type="submit" disabled={!message.trim() || isLoading}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AiAssistant;
